import { Button, Flex, FlexItem } from '@patternfly/react-core'
import {
  TableComposable,
  Tbody,
  Td,
  Tr,
  TableVariant,
} from '@patternfly/react-table'
import * as React from 'react'
import { useCallback } from 'react'
import { NavLink } from 'react-router-dom'

import { RequireFeatureFlag } from '@app/components/feature_flags/require-feature-flag'
import { useFeatureFlags } from '@app/domain/feature-flags/feature-flags.hooks'
import { FeatureFlagId } from '@app/domain/feature-flags/feature-flags.interface'
import InteractionStatus from '@app/domain/interaction/components/interaction-status.component'
import {
  InteractionStatusId,
  TransmissionStatusId,
} from '@app/domain/interaction/interaction.interface'
import PersonName from '@app/domain/person/components/person-name.component'
import type { INamedPerson } from '@app/domain/person/person.interface'
import { RemoteWorkflowStatus } from '@app/domain/program/components/workflow-status.component'
import { getInteractionLabel } from '@app/domain/program/program.helpers'
import type { IProgramInteractionRecord } from '@app/domain/program/program.interface'
import {
  IInteractionStatusId,
  ProgramInteractionTypeId,
} from '@app/domain/program/program.interface'
import { formatIsoDateTimeOffset } from '@app/i18n/formats'
import type { ISorting } from '@app/lib/sorting'
import { ActionLink } from '@app/utils/styles/action-link'

import { CommunicationStatusDetails } from './communication-status-detail'
import { DisplayColumn } from './configured-interactions-table/display-column'
import { ConfiguredInteractionsTableHeader } from './configured-interactions-table-header'

interface InteractionStatusButtonProps {
  interaction: IProgramInteractionRecord
  onSendInteractionToIntakeAnywhere: (
    patientId: string,
    programInteractionId: string,
  ) => void
  onRevokeInteraction: (patientId: string, programInteractionId: string) => void
}

export const InteractionStatusButton = ({
  interaction,
  onSendInteractionToIntakeAnywhere,
  onRevokeInteraction,
}: InteractionStatusButtonProps) => {
  const handleSendInteractionToIntakeAnywhere = useCallback(
    (patientId, programInteractionId: string) => {
      onSendInteractionToIntakeAnywhere(patientId, programInteractionId)
    },
    [onSendInteractionToIntakeAnywhere],
  )

  const handleRevokeInteraction = useCallback(
    (patientId, programInteractionId: string) => {
      onRevokeInteraction(patientId, programInteractionId)
    },
    [onRevokeInteraction],
  )

  const isInactive = interaction.status === InteractionStatusId.Removed
  const isInProgress = interaction.status === InteractionStatusId.InProgress
  const isCancelled = interaction.status === InteractionStatusId.Cancelled
  const isRemote =
    interaction.interaction.interactionType ===
    ProgramInteractionTypeId.RemoteInteraction
  const isTransmitted =
    interaction.status === InteractionStatusId.DelegatedToRemote
  const isFailed =
    interaction.transmissionStatus === TransmissionStatusId.Failure
  const isExpired = interaction.status === InteractionStatusId.Expired

  if (!isRemote || isInactive) {
    return null
  }

  const isConsentGiven =
    interaction.consentsToEmail || interaction.consentsToSms

  const showRedeliver =
    isRemote &&
    !isInProgress &&
    isConsentGiven &&
    (isFailed || isExpired || isTransmitted)

  const showInitialDelivery =
    isRemote &&
    !isInProgress &&
    !showRedeliver &&
    !isCancelled &&
    isConsentGiven &&
    !isTransmitted

  const showRevoke = isTransmitted && !isFailed

  return (
    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
      {showRedeliver ? (
        <Button
          variant="primary"
          onClick={() =>
            handleSendInteractionToIntakeAnywhere(
              interaction.patientId,
              interaction.interaction.id,
            )
          }
          isSmall
        >
          Re-Deliver
        </Button>
      ) : null}

      {showInitialDelivery ? (
        <Button
          variant="primary"
          onClick={() =>
            handleSendInteractionToIntakeAnywhere(
              interaction.patientId,
              interaction.interaction.id,
            )
          }
          isSmall
        >
          Deliver
        </Button>
      ) : null}

      {showRevoke ? (
        <Button
          variant="secondary"
          onClick={() =>
            handleRevokeInteraction(
              interaction.patientId,
              interaction.interaction.id,
            )
          }
          isSmall
        >
          Revoke
        </Button>
      ) : null}
    </div>
  )
}

interface ConfiguredInteractionsTableProps {
  interactions: IProgramInteractionRecord[]
  onPatientSelected: (patientId: string) => void
  onSendInteractionToIntakeAnywhere: (
    patientId: string,
    programInteractionId: string,
  ) => void
  onRevokeInteraction: (patientId: string, programInteractionId: string) => void
  sorting: ISorting
}

export const ConfiguredInteractionsTable = ({
  interactions,
  onPatientSelected,
  onRevokeInteraction,
  onSendInteractionToIntakeAnywhere,
  sorting,
}: ConfiguredInteractionsTableProps) => {
  const featureFlags = useFeatureFlags()

  const remoteInteractionEnabled = featureFlags.isFeatureFlagEnabled(
    FeatureFlagId.RemoteInteraction,
  )
  const columnNames = [
    { title: 'Interaction', value: 'type' },
    { title: 'Label', value: 'label' },
    { title: 'MRN', value: 'mrn' },
    { title: 'Patient Name', value: 'name' },
    ...(remoteInteractionEnabled
      ? [{ title: 'Communication Status', value: 'communication_status' }]
      : []),
    { title: 'Status', value: 'status' },
    { title: 'Status Updated', value: 'status_updated_at' },
    ...(remoteInteractionEnabled
      ? [
          { title: 'Delivery', value: 'delivery' },
          { title: 'Delivery Updated', value: 'delivery_updated_at' },
        ]
      : []),

    { title: 'Action', value: 'action', sortable: false },
  ]
  const getColumnTitle = (value: string) => {
    const column = columnNames.find((col) => col.value === value)
    return column?.title || value
  }

  const handlePatientClick = useCallback(
    (patientId: string) => () => onPatientSelected(patientId),
    [onPatientSelected],
  )

  const renderViewResultsButton = useCallback(
    (interaction: IProgramInteractionRecord) => {
      if (interaction.visitId) {
        return (
          <NavLink to={`/clinic/visit/${interaction.visitId}`}>
            <Button variant="secondary" isSmall>
              View Results
            </Button>
          </NavLink>
        )
      }
      if (interaction.remoteInteractionId) {
        return (
          <NavLink
            to={`/clinic/interaction/${interaction.remoteInteractionId}`}
          >
            <Button variant="secondary" isSmall>
              View Results
            </Button>
          </NavLink>
        )
      }

      return null
    },
    [],
  )

  const renderDeliveredAt = (programInteraction: IProgramInteractionRecord) => {
    return programInteraction.deliveredAt
      ? formatIsoDateTimeOffset(programInteraction.deliveredAt)
      : ''
  }

  const renderDelivered = (programInteraction: IProgramInteractionRecord) => {
    if (
      programInteraction.status === InteractionStatusId.Removed ||
      programInteraction.interaction.status === IInteractionStatusId.Removed
    ) {
      return ''
    } else {
      return (
        <DisplayColumn
          programInteraction={programInteraction}
          key={`${programInteraction.patientId}-${programInteraction.interaction.id}`}
        />
      )
    }
  }

  const renderStatusUpdatedAt = (
    programInteraction: IProgramInteractionRecord,
  ) => {
    if (
      programInteraction.status === InteractionStatusId.Removed ||
      programInteraction.interaction.status === IInteractionStatusId.Removed
    ) {
      return programInteraction.interaction.removedAt
        ? formatIsoDateTimeOffset(programInteraction.interaction.removedAt)
        : '-'
    } else if (programInteraction.status === InteractionStatusId.Completed) {
      return programInteraction.interactionCompletedAt
        ? formatIsoDateTimeOffset(programInteraction.interactionCompletedAt)
        : '-'
    } else if (
      programInteraction.status === InteractionStatusId.DelegatedToRemote
    ) {
      return programInteraction.remoteWorkflowStatusUpdatedAt
        ? formatIsoDateTimeOffset(
            programInteraction.remoteWorkflowStatusUpdatedAt,
          )
        : '-'
    } else {
      return programInteraction.remoteWorkflowStatusUpdatedAt
        ? formatIsoDateTimeOffset(
            programInteraction.remoteWorkflowStatusUpdatedAt,
          )
        : '-'
    }
  }

  const renderStatus = (programInteraction: IProgramInteractionRecord) => {
    if (
      programInteraction.status === InteractionStatusId.Removed ||
      programInteraction.interaction.status === IInteractionStatusId.Removed
    ) {
      return `Removed ${
        programInteraction.interaction.removedAt
          ? ` (${formatIsoDateTimeOffset(
              programInteraction.interaction.removedAt,
            )})`
          : ''
      }`
    } else if (
      programInteraction.status === InteractionStatusId.DelegatedToRemote
    ) {
      if (
        programInteraction.transmissionStatus === TransmissionStatusId.Failure
      ) {
        return (
          <div style={{ color: 'green' }}>
            {programInteraction.remoteWorkflowStatus ? (
              <RemoteWorkflowStatus
                status={programInteraction.remoteWorkflowStatus}
              />
            ) : (
              <InteractionStatus status={InteractionStatusId.Pending} />
            )}
          </div>
        )
      } else {
        return (
          <div style={{ color: 'green' }}>
            <RemoteWorkflowStatus
              status={programInteraction.remoteWorkflowStatus}
            />
          </div>
        )
      }
    } else {
      return (
        <div style={{ color: 'green' }}>
          <InteractionStatus status={programInteraction.status} />
        </div>
      )
    }
  }

  return (
    <TableComposable
      variant={TableVariant.compact}
      aria-label="Program Interactions"
    >
      <ConfiguredInteractionsTableHeader
        sorting={sorting}
        columnNames={columnNames}
      />
      <Tbody>
        {interactions.map((interactionRecord, index) => {
          const namedPerson: INamedPerson = {
            familyName: interactionRecord.familyName,
            givenNames: interactionRecord.givenName,
          }

          return (
            <Tr
              key={`${interactionRecord.interaction.id}-${index}`}
              isHoverable
            >
              <Td dataLabel={getColumnTitle('type')}>
                {getInteractionLabel(
                  interactionRecord.interaction.interactionType,
                )}
              </Td>
              <Td dataLabel={getColumnTitle('label')}>
                {interactionRecord.interaction.label}
              </Td>
              <Td dataLabel={getColumnTitle('mrn')}>
                <ActionLink
                  onClick={handlePatientClick(interactionRecord.patientId)}
                >
                  {interactionRecord.mrn}
                </ActionLink>
              </Td>
              <Td dataLabel={getColumnTitle('name')}>
                <ActionLink
                  onClick={handlePatientClick(interactionRecord.patientId)}
                >
                  <PersonName namedPerson={namedPerson} />
                </ActionLink>
              </Td>

              <RequireFeatureFlag feature={FeatureFlagId.RemoteInteraction}>
                <Td dataLabel={getColumnTitle('communication_status')}>
                  <Flex>
                    <FlexItem>
                      <CommunicationStatusDetails
                        emailAddress={interactionRecord.emailAddress}
                        consentsToEmail={interactionRecord.consentsToEmail}
                        phoneNumber={interactionRecord.phoneNumber}
                        phoneNumberType={interactionRecord.phoneNumberType}
                        consentsToSms={interactionRecord.consentsToSms}
                      />
                    </FlexItem>
                  </Flex>
                </Td>
              </RequireFeatureFlag>
              <Td dataLabel={getColumnTitle('status')}>
                {renderStatus(interactionRecord)}
              </Td>
              <Td dataLabel={getColumnTitle('status_updated_at')}>
                {renderStatusUpdatedAt(interactionRecord)}
              </Td>
              <RequireFeatureFlag feature={FeatureFlagId.RemoteInteraction}>
                <Td dataLabel={getColumnTitle('delivery')}>
                  {renderDelivered(interactionRecord)}
                </Td>
                <Td dataLabel={getColumnTitle('delivery_updated_at')}>
                  {interactionRecord.interaction.interactionType ===
                  ProgramInteractionTypeId.RemoteInteraction
                    ? renderDeliveredAt(interactionRecord)
                    : '-'}
                </Td>
              </RequireFeatureFlag>
              <Td dataLabel={getColumnTitle('action')}>
                {interactionRecord.interaction.status ===
                IInteractionStatusId.Removed ? (
                  interactionRecord.interactionCompletedAt ? (
                    renderViewResultsButton(interactionRecord)
                  ) : null
                ) : interactionRecord.interactionCompletedAt ? (
                  renderViewResultsButton(interactionRecord)
                ) : (
                  <InteractionStatusButton
                    interaction={interactionRecord}
                    onRevokeInteraction={onRevokeInteraction}
                    onSendInteractionToIntakeAnywhere={
                      onSendInteractionToIntakeAnywhere
                    }
                  />
                )}
              </Td>
            </Tr>
          )
        })}
      </Tbody>
    </TableComposable>
  )
}
